import React, { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Ship, LayoutDashboard, Package, Wallet, Users as UsersIcon, Settings as SettingsIcon, LogOut, ChevronDown, Briefcase, Plane, Archive, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ChangePassword } from "@/components/ChangePassword";
import { ROLE_BADGE, FIN_ROLES, COMPLETED_ROLES } from "@/lib/stages";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/dashboard",   label: "Dashboard",  icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/shipments",   label: "Shipments",  icon: Package,         testid: "nav-shipments", children: [
    { to: "/shipments?type=brokerage",  label: "Brokerage",  icon: Briefcase, testid: "nav-shipments-brokerage" },
    { to: "/shipments?type=forwarding", label: "Forwarding", icon: Plane,     testid: "nav-shipments-forwarding" },
    { to: "/shipments?type=completed",  label: "Completed",  icon: CheckCircle2, testid: "nav-shipments-completed", roles: COMPLETED_ROLES },
    { to: "/shipments?type=archive",    label: "Archive",    icon: Archive,   testid: "nav-shipments-archive", roles: ["admin"] },
  ] },
  { to: "/finance",     label: "Finance",    icon: Wallet,          testid: "nav-finance", roles: FIN_ROLES },
  { to: "/users",       label: "Users",      icon: UsersIcon,       testid: "nav-users", roles: ["admin"] },
  { to: "/settings",    label: "Settings",   icon: SettingsIcon,    testid: "nav-settings", roles: ["admin"] },
];

const linkCls = (active) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
  active ? "bg-blue-600/15 text-sky-300 border border-blue-500/30" : "text-slate-300 hover:bg-slate-800/60 hover:text-white border border-transparent"}`;

const useIsActive = () => {
  const loc = useLocation();
  return (to) => {
    const [path, qs] = to.split("?");
    if (!qs) return loc.pathname.startsWith(path);
    const want = new URLSearchParams(qs).get("type");
    return loc.pathname === path && (new URLSearchParams(loc.search).get("type") || "brokerage") === want;
  };
};

function NavGroup({ item, isActive, role }) {
  const [open, setOpen] = useState(true);
  const Icon = item.icon;
  return (
    <div>
      <button data-testid={item.testid} onClick={() => setOpen(!open)}
              className={`w-full justify-between ${linkCls(false)} ${isActive(item.to) ? "text-white" : ""}`}>
        <span className="flex items-center gap-3"><Icon size={16} /> {item.label}</span>
        <ChevronDown size={14} className={`transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && (
        <div className="mt-1 ml-4 pl-3 border-l border-slate-800 space-y-1">
          {item.children.filter((c) => !c.roles || c.roles.includes(role)).map((c) => (
            <Link key={c.to} to={c.to} data-testid={c.testid} className={linkCls(isActive(c.to))}>
              <c.icon size={15} /> {c.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isActive = useIsActive();
  const visible = NAV.filter(n => !n.roles || n.roles.includes(user?.role));

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#0B0F19] text-slate-100">
      <aside className="w-[260px] shrink-0 border-r border-slate-800 bg-[#0B0F19] hidden md:flex flex-col sticky top-0 h-screen overflow-y-auto">
        <div className="h-16 px-5 flex items-center gap-3 border-b border-slate-800">
          <div className="w-9 h-9 rounded-md bg-gradient-to-br from-sky-500 to-blue-700 flex items-center justify-center">
            <Ship size={18} />
          </div>
          <div>
            <div className="font-display font-bold text-base tracking-wide leading-tight">LEAD ASIA</div>
            <div className="text-[10px] text-slate-400 uppercase tracking-widest">Brokerage Ops</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {visible.map((n) => n.children ? <NavGroup key={n.to} item={n} isActive={isActive} role={user?.role} /> : (
            <NavLink key={n.to} to={n.to} data-testid={n.testid} className={({ isActive: a }) => linkCls(a)}>
              <n.icon size={16} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Signed in</div>
          <div className="text-sm font-medium truncate">{user?.name}</div>
          <div className="text-xs text-slate-400 truncate mb-2">{user?.email}</div>
          <span data-testid="user-role-badge" className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${ROLE_BADGE[user?.role] || ""}`}>
            {user?.role}
          </span>
          <ChangePassword />
          <Button data-testid="button-logout" onClick={onLogout} size="sm"
                  className="w-full mt-3 bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700">
            <LogOut size={14} className="mr-2" /> Logout
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <header className="h-16 border-b border-slate-800 px-4 sm:px-6 flex items-center justify-between bg-[#0B0F19]/80 backdrop-blur sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="md:hidden flex items-center gap-2">
              <Ship size={18} className="text-sky-400" />
              <span className="font-display font-bold">LEAD ASIA</span>
            </Link>
            <div className="hidden md:block">
              <div className="text-xs uppercase tracking-widest text-slate-400">Operations Portal</div>
              <div className="font-display font-semibold text-lg">Lead Asia Cargo Inc.</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-slate-300">{user?.name}</span>
            <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${ROLE_BADGE[user?.role] || ""}`}>
              {user?.role}
            </span>
            <ChangePassword compact />
            <button data-testid="mobile-button-logout" onClick={onLogout} aria-label="Logout"
                    className="md:hidden p-2 rounded-md text-rose-300 border border-slate-700 hover:bg-slate-800 transition-colors">
              <LogOut size={14} />
            </button>
          </div>
        </header>
        <nav data-testid="mobile-nav" className="md:hidden flex items-center gap-1 overflow-x-auto px-4 py-2 border-b border-slate-800 bg-[#0B0F19]">
          {visible.flatMap((n) => n.children || [n]).filter((n) => !n.roles || n.roles.includes(user?.role)).map(({ to, label, icon: Icon, testid }) => (
            <Link key={to} to={to} data-testid={`mobile-${testid}`}
              className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition-colors ${
                isActive(to) ? "bg-blue-600/15 text-sky-300 border border-blue-500/30" : "text-slate-300 border border-transparent"}`}>
              <Icon size={14} /> {label}
            </Link>
          ))}
        </nav>
        <div className="p-4 sm:p-6 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
